import * as React from 'react';
import { useForm } from 'react-hook-form';
import { SheetIcon } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { type Filters } from 'hooks/store/stock-balance';
import { useProductOptions, useCompanyOptions } from 'hooks/api/options';
import { MultiSelectGs, InputGs } from 'components/ui/Forms';
import DateRangePickerGs from 'components/ui/Forms/dateRangePickerGs';
import { Button as ButtonGs } from 'components/ui/ButtonGs';
import { Form, FormField } from 'components/ui/form';

import {
  RefineReportParamsDrawer,
  RefineReportParamsDrawerRef,
} from './refine-report-params-drawer';
import { formSchema, type FormSchema } from './formSchema';
import { createReportParams, createFilterParams } from './utils';

type Props = React.HTMLAttributes<HTMLDivElement> & {
  onSubmitFilters: (filters: Filters) => void;
  onResetFilters: () => void;
  onRequestReport: (reportParams: Record<string, unknown>) => void;
};

export const FiltersForm = ({
  onSubmitFilters,
  onResetFilters,
  onRequestReport,
}: Props) => {
  const productOptions = useProductOptions();
  const companyOptions = useCompanyOptions();

  const refineReportDrawerRef = React.useRef<RefineReportParamsDrawerRef>(null);

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const onFormSubmit = React.useCallback(
    (formData: FormSchema) => {
      const filtersParams = createFilterParams({
        formData,
      });
      onSubmitFilters(filtersParams);
    },
    [onSubmitFilters]
  );

  const onFormReset = React.useCallback(() => {
    form.reset(
      {
        agDate: undefined,
        companyIds: [],
        productIds: [],
        invoiceNumber: '',
      },
      {
        keepValues: false,
      }
    );
    onResetFilters();
  }, [form]);

  const onRefineReportDrawerSubmit = React.useCallback((columns: string[]) => {
    const reportParams =  createReportParams({
      formData: form.getValues(),
      columns,
    });
    onRequestReport(reportParams)
  }, [form, onRequestReport]);


  return (
    <>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onFormSubmit)}
          className={'flex flex-col w-full'}
        >
          <div
            className={'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4'}
          >
            <FormField
              control={form.control}
              name={'productIds'}
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label={'Produtos'}
                  maxCount={1}
                  options={productOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'max-w-[400px] md:max-w-[200px] xl:max-w-[240px]',
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name={'invoiceNumber'}
              render={({ field }) => (
                <InputGs {...field} type="text" label={'NF'} />
              )}
            />
            <FormField
              control={form.control}
              name={'companyIds'}
              render={({ field }) => (
                <MultiSelectGs
                  form={form}
                  fieldName={field.name}
                  fieldValue={field.value}
                  label={'Clientes'}
                  maxCount={1}
                  options={companyOptions.multiSelectOptions}
                  classNames={{
                    selectedBadge:
                      'max-w-[400px] md:max-w-[200px] xl:max-w-[240px]',
                  }}
                />
              )}
            />
            <FormField
              control={form.control}
              name={'agDate'}
              render={({ field }) => (
                <DateRangePickerGs
                  label={'Data AG'}
                  onSelect={field.onChange}
                  selected={{
                    from: field.value?.from,
                    to: field.value?.to,
                  }}
                />
              )}
            />
          </div>
          <div className={'flex gap-4 mt-4'}>
            <ButtonGs.Root
              type="button"
              variant="secondary"
              size="sm"
              onClick={onFormReset}
            >
              Limpar filtros
            </ButtonGs.Root>
            <ButtonGs.Root type="submit" size="sm">
              Filtrar
            </ButtonGs.Root>
            <ButtonGs.Root
              type="button"
              size="sm"
              className="ml-auto"
              onClick={() => refineReportDrawerRef.current?.open()}
            >
              <SheetIcon />
              <span>Exportar</span>
            </ButtonGs.Root>
          </div>
        </form>
      </Form>
      <RefineReportParamsDrawer
        ref={refineReportDrawerRef}
        onSubmit={onRefineReportDrawerSubmit}
      />
    </>
  );
};
